import React, { useEffect, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControlLabel,
  Switch,
  Link,
} from '@mui/material'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  LabelList,
} from 'recharts'
import RefreshIcon from '@mui/icons-material/Refresh'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { disputeInsightsAPI } from '../services/api'
import DisputeReviewSection from '../components/DisputeReviewSection'
import Panel from '../components/Panel'

const SEVERITY_COLOR = {
  high: 'error',
  medium: 'warning',
  low: 'info',
}

// A real run pages through the Izzi API, so poll rather than guess a delay
export const POLL_INTERVAL_MS = 5000
export const POLL_MAX_ATTEMPTS = 36

const BAR_ROW_HEIGHT = 36
const RECENT_TICKETS_SHOWN = 10
// Desktop height of the charts row; long content scrolls inside its card instead
const CHARTS_ROW_HEIGHT = 640

const PROGRESS_LABELS = {
  no_reply: 'No reply',
  replied: 'Replied',
  form: 'Form submitted',
  sub_agent: 'In sub-agent',
  dispute: 'Dispute raised',
  completed: 'Completed',
  error: 'Error',
}

const STATUS_CHIPS = {
  complete: { label: 'Complete', color: 'success' },
  error: { label: 'Error', color: 'error' },
}

const FUNNEL_LABELS = {
  opened: 'Opened AI dispute',
  replied: 'Replied to the agent',
  form: 'Submitted a form',
  sub_agent: 'Reached a sub-agent',
  dispute: 'Dispute raised',
  completed: 'Completed',
}

function formatDate(iso, options = { day: 'numeric', month: 'short' }) {
  return new Date(iso).toLocaleDateString(undefined, options)
}

// Calendar days in the period, both ends included (the backend's own count when present)
export function periodDays(snapshot) {
  if (snapshot.period_days) return snapshot.period_days
  const day = (iso) => Date.UTC(...iso.slice(0, 10).split('-').map((n, i) => (i === 1 ? n - 1 : +n)))
  return Math.round((day(snapshot.end_date) - day(snapshot.start_date)) / 86400000) + 1
}

function signedDays(value) {
  return `${value > 0 ? '+' : ''}${value}d`
}

export function aiVsManualLabel(delta, aiResolved, manualResolved) {
  // An empty side averages 0, which would make the other side look slow
  if (aiResolved === 0 || manualResolved === 0) return 'Not enough resolved disputes to compare'
  if (!delta) return 'AI and manual resolve at the same pace'
  const days = Math.abs(delta)
  return delta < 0 ? `AI resolves ${days}d faster` : `AI resolves ${days}d slower`
}

function weeklyTrendData(snapshot) {
  return snapshot.trend.map((count, week) => {
    const weekStart = new Date(snapshot.start_date)
    weekStart.setDate(weekStart.getDate() + week * 7)
    return { week: formatDate(weekStart), count }
  })
}

function StatTile({ label, value, sublabel }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h4">{value}</Typography>
        {sublabel && (
          <Typography variant="caption" color="text.secondary">
            {sublabel}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

function AttentionPanel({ items }) {
  if (!items || items.length === 0) {
    return (
      <Alert severity="success">Nothing needs attention right now.</Alert>
    )
  }

  return (
    <Paper variant="outlined">
      <List>
        {items.map((item, index) => (
          <React.Fragment key={`${item.category}-${index}`}>
            {index > 0 && <Divider component="li" />}
            <ListItem>
              <WarningAmberIcon
                color={SEVERITY_COLOR[item.severity] || 'action'}
                sx={{ mr: 2 }}
              />
              <ListItemText
                primary={item.summary}
                secondary={item.detail}
              />
              {item.ticket_id && (
                <Button
                  component={RouterLink}
                  to={`/tickets/${item.ticket_id}`}
                  size="small"
                  sx={{ mr: 1, whiteSpace: 'nowrap' }}
                >
                  Open ticket
                </Button>
              )}
              {(item.related_ticket_ids || []).map((id) => (
                <Button
                  key={id}
                  component={RouterLink}
                  to={`/tickets/${id}`}
                  size="small"
                  sx={{ minWidth: 0, whiteSpace: 'nowrap' }}
                >
                  #{id}
                </Button>
              ))}
              <Chip
                label={item.severity}
                color={SEVERITY_COLOR[item.severity] || 'default'}
                size="small"
              />
            </ListItem>
          </React.Fragment>
        ))}
      </List>
    </Paper>
  )
}

// With `fill`, the chart stretches to its card and only scrolls when the rows need more room
function StackedBarsByCategory({ data, dataKey, labelWidth, fill = false }) {
  const neededHeight = Math.max(data.length, 1) * BAR_ROW_HEIGHT + 60
  return (
    <ResponsiveContainer width="100%" height={fill ? '100%' : neededHeight} minHeight={neededHeight}>
      <BarChart data={data} layout="vertical">
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" allowDecimals={false} />
        <YAxis type="category" dataKey={dataKey} width={labelWidth} interval={0} tick={{ fontSize: 12 }} />
        <Tooltip isAnimationActive={false} />
        <Legend />
        <Bar dataKey="manual_count" name="Manual" stackId="a" fill="#90a4ae" maxBarSize={40} />
        <Bar dataKey="ai_count" name="AI" stackId="a" fill="#1976d2" maxBarSize={40} />
      </BarChart>
    </ResponsiveContainer>
  )
}

export function RecentTickets({ tickets }) {
  const [showNoReply, setShowNoReply] = useState(false)
  const rows = tickets.filter((t) => showNoReply || t.progress !== 'no_reply').slice(0, RECENT_TICKETS_SHOWN)

  return (
    <Panel
      title="Recent tickets"
      action={
        <FormControlLabel
          control={<Switch size="small" checked={showNoReply} onChange={(e) => setShowNoReply(e.target.checked)} />}
          label={<Typography variant="caption">Include no-reply</Typography>}
        />
      }
    >
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No recent tickets.</Typography>
      ) : (
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Ticket</TableCell>
              <TableCell>Raised by</TableCell>
              <TableCell>Opened</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Progress</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((t) => {
              const chip = STATUS_CHIPS[t.status] || { label: 'Open', color: 'default' }
              const progress = PROGRESS_LABELS[t.progress] || t.progress
              return (
                <TableRow key={t.ticket_id}>
                  <TableCell>
                    <Link component={RouterLink} to={`/tickets/${t.ticket_id}`}>#{t.ticket_id}</Link>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.created_by || '—'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(t.created_at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Chip size="small" label={chip.label} color={chip.color} />
                    {t.awaiting_ai && <Chip size="small" label="Awaiting AI" color="warning" sx={{ ml: 0.5 }} />}
                  </TableCell>
                  <TableCell>
                    {progress}
                    {t.sub_agent && (
                      <Typography variant="caption" color="text.secondary" display="block">{t.sub_agent}</Typography>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </Panel>
  )
}

function TicketFunnel({ stages }) {
  const opened = stages.find((s) => s.stage === 'opened')?.count || 0
  const replied = stages.find((s) => s.stage === 'replied')?.count || 0
  const data = stages.map((s) => ({
    label: FUNNEL_LABELS[s.stage] || s.stage,
    count: s.count,
    share: opened ? `${s.count} (${Math.round((s.count / opened) * 100)}%)` : `${s.count}`,
  }))

  return (
    <Panel title="Where users drop off">
      {opened > 0 && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {opened - replied} of {opened} AI dispute tickets ({Math.round(((opened - replied) / opened) * 100)}%) were
          opened and left without the user replying. They aren't flagged.
        </Typography>
      )}
      <ResponsiveContainer width="100%" height="100%" minHeight={data.length * BAR_ROW_HEIGHT + 40}>
        <BarChart data={data} layout="vertical" margin={{ right: 80 }}>
          <XAxis type="number" hide allowDecimals={false} />
          <YAxis type="category" dataKey="label" width={170} interval={0} tick={{ fontSize: 12 }} />
          <Tooltip isAnimationActive={false} />
          <Bar dataKey="count" name="Tickets" fill="#1976d2">
            <LabelList dataKey="share" position="right" style={{ fontSize: 12 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Panel>
  )
}

export default function DisputeInsightsPage() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [runStatus, setRunStatus] = useState(null)
  const pollTimer = useRef(null)
  const mounted = useRef(true)

  const load = async () => {
    try {
      setError(null)
      const result = await disputeInsightsAPI.getLatest()
      setData(result)
      return result
    } catch (err) {
      if (err?.response?.status === 404) {
        setError('No dispute insights report yet — click Run now to compute the first one.')
      } else {
        setError('Failed to load dispute insights.')
      }
      return null
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    mounted.current = true
    load()
    return () => {
      mounted.current = false
      clearTimeout(pollTimer.current)
    }
  }, [])

  const pollForNewReport = (previousComputedAt, attempt = 1) => {
    pollTimer.current = setTimeout(async () => {
      let result = null
      try {
        result = await disputeInsightsAPI.getLatest()
      } catch {
        // No report yet; keep polling
      }
      // The page may have been left while the request was in flight
      if (!mounted.current) return
      if (result && result.last_computed !== previousComputedAt) {
        setData(result)
        setError(null)
        setRunStatus(null)
      } else if (attempt >= POLL_MAX_ATTEMPTS) {
        setRunStatus('timeout')
      } else {
        pollForNewReport(previousComputedAt, attempt + 1)
      }
    }, POLL_INTERVAL_MS)
  }

  const handleRunNow = async () => {
    clearTimeout(pollTimer.current)
    // Disable the button before the POST so a double click can't trigger twice
    setRunStatus('running')
    try {
      await disputeInsightsAPI.triggerRefresh()
    } catch {
      if (mounted.current) setRunStatus('failed')
      return
    }
    if (mounted.current) pollForNewReport(data?.last_computed ?? null)
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    )
  }

  const report = data?.report
  const snapshot = report?.snapshot
  const running = runStatus === 'running'

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5">Dispute Insights</Typography>
          {snapshot?.start_date && snapshot?.end_date && (
            <Typography variant="body2" color="text.secondary">
              Last {periodDays(snapshot)} days: {formatDate(snapshot.start_date)} – {formatDate(snapshot.end_date, { day: 'numeric', month: 'short', year: 'numeric' })}
            </Typography>
          )}
        </Box>
        <Button
          startIcon={running ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={handleRunNow}
          disabled={running}
          variant="outlined"
        >
          {running ? 'Running…' : 'Run now'}
        </Button>
      </Box>

      {running && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Computing a fresh report — this usually takes about a minute. The page updates when it's ready.
        </Alert>
      )}
      {runStatus === 'timeout' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Still no new report after 3 minutes. Check Admin → System → Background Jobs for the run's status.
        </Alert>
      )}
      {runStatus === 'failed' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn't start a run. Check Admin → System → Background Jobs.
        </Alert>
      )}
      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {!snapshot && (
        <Box mb={3}>
          <DisputeReviewSection />
        </Box>
      )}

      {snapshot && (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile
                label="Disputes raised"
                value={snapshot.total_raised_count}
                sublabel={`${snapshot.total_raised_delta > 0 ? '+' : ''}${snapshot.total_raised_delta}% vs. previous ${periodDays(snapshot)} days`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile
                label="Resolution rate"
                value={`${snapshot.resolution_rate}%`}
                sublabel={`${snapshot.resolved_count} resolved, ${snapshot.unresolved_count} open`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile
                label="Avg. resolution time"
                value={`${snapshot.avg_resolution_days} days`}
                sublabel={`${signedDays(snapshot.avg_resolution_days_delta)} vs. previous ${periodDays(snapshot)} days`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile
                label="AI / manual disputes"
                value={`${snapshot.ai_created_count} / ${snapshot.manual_created_count}`}
                sublabel={aiVsManualLabel(snapshot.ai_vs_manual_days_delta, snapshot.ai_resolved_count, snapshot.manual_resolved_count)}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom>
            Needs attention
          </Typography>
          <Box mb={3}>
            <AttentionPanel items={report.attention} />
          </Box>

          {report.ticket_funnel?.length > 0 && (
            <Box mb={3}>
              <TicketFunnel stages={report.ticket_funnel} />
            </Box>
          )}

          <Box mb={3}>
            <DisputeReviewSection />
          </Box>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection="column" gap={2} sx={{ height: { md: CHARTS_ROW_HEIGHT } }}>
              <Panel
                title="Disputes raised per week"
                sx={{ height: 'auto', flex: report.recent_tickets?.length ? '0 0 auto' : 1 }}
              >
                <ResponsiveContainer width="100%" height="100%" minHeight={250}>
                  <LineChart data={weeklyTrendData(snapshot)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip isAnimationActive={false} labelFormatter={(week) => `Week of ${week}`} />
                    <Line type="monotone" dataKey="count" name="Disputes" stroke="#1976d2" />
                  </LineChart>
                </ResponsiveContainer>
              </Panel>
              {report.recent_tickets?.length > 0 && (
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <RecentTickets tickets={report.recent_tickets} />
                </Box>
              )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ height: { md: CHARTS_ROW_HEIGHT } }}>
                <Panel title="By reason code (AI vs. manual)">
                  <StackedBarsByCategory data={snapshot.by_type} dataKey="type" labelWidth={190} fill />
                </Panel>
              </Box>
            </Grid>
          </Grid>

          <Panel title="Top resellers by dispute volume">
            <StackedBarsByCategory data={snapshot.by_reseller} dataKey="reseller_name" labelWidth={200} fill />
          </Panel>

          {data?.last_computed && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Last computed: {new Date(data.last_computed).toLocaleString()}
            </Typography>
          )}
        </>
      )}
    </Box>
  )
}
